import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress  from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormGroup from '@mui/material/FormGroup';
import InputLabel from '@mui/material/InputLabel';
import Paper, { PaperProps } from '@mui/material/Paper';
import Draggable from 'react-draggable';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import React, { ReactElement, useEffect, useState } from 'react';
import {  uuid } from '../../../../utils/utils';
import { toast } from 'react-toastify';
import { useWorkspaceContext } from '../../../../context/workspaceContext';
import { CanvasEnvironment } from '../../../../utils/canvas/canvas-environment';
import * as CanvasActions from '../../../../utils/canvas/canvas-actions';
import { PsfTemplate, PsfTemplateItem } from '../../../../@types/occurrence-dna';
import DatePicker from 'react-datepicker';

import 'react-datepicker/dist/react-datepicker.css';

import EventSequenceService from '../../../../services/event-sequence.service';
import { CanvasActionType } from '../../../../@types/canvas-action-type';
import ConditionBuilderComponent, { Condition } from '../../../form-components/ConditionBuilder.component';

type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];

function forceUTCDate(referenceDate: Value): string | null { 
    if (referenceDate === null) {
        return null;
    }
    if (Array.isArray(referenceDate)) {
        let start = referenceDate[0] as Date;
        let end = referenceDate[1] as Date;
        let tzOffset = start.getTimezoneOffset();
        let startDate = new Date();
        startDate.setTime(start.getTime() - tzOffset * 60 * 1000);
        let endDate = new Date();
        endDate.setTime(end.getTime() - tzOffset * 60 * 1000);
        return JSON.stringify([startDate, endDate]);
    }
    let tzOffset = referenceDate.getTimezoneOffset();
    let date = new Date();
    date.setTime(referenceDate.getTime() - tzOffset * 60 * 1000);
    return date.toJSON();
}


function PaperComponent(props: PaperProps): ReactElement {
    const nodeRef = React.useRef(null);
    return (
        <Draggable nodeRef={nodeRef} handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

export default function OdnaStatComponentModal(props: { open: boolean, env: CanvasEnvironment | null, onClose: () => void }): ReactElement {
    const { open, env, onClose } = props;
    const [esdPsfTemplate, setEsdPsfTemplate] = useState<PsfTemplate | null>(null);
    const [occurrenceTemplateKeys, setOccurrenceTemplateKeys] = useState<string[]>([]);
    const [selectedPsfItems, setSelectedPsfItems] = useState<PsfTemplateItem[]>([]);

    const [startValue, onChangeStartValue] = useState<Value>(new Date('2010-01-01T00:00:00.000'));
    const [endValue, onChangeEndValue] = useState<Value>(new Date('2050-01-01T00:00:00.000'));
    const [condition, setCondition] = useState<Condition | null>(null);
    const [isRunning, setIsRunning] = useState<boolean>(false);
    const { setCurrentCanvasAction } = useWorkspaceContext();
    const uuidRef = React.useRef(uuid());

    const onChangeCondition = (newCondition: Condition | null): void => {
        setCondition(newCondition);
    }

    useEffect(() => {
        if (open) {
            setIsRunning(true);
            setCondition(null);
            let eventSequenceId = env!.eventSequence!.id;
            let esdPsfTemplatePromise = EventSequenceService.getPsfTemplate(eventSequenceId);
            let templateKeysPromise = EventSequenceService.fetchOccurrenceTemplateKeys();
            Promise.all([esdPsfTemplatePromise, templateKeysPromise]).then((response) => {
                setEsdPsfTemplate(response[0]);
                setOccurrenceTemplateKeys(response[1]);
            }).catch((error) => {
                toast.error(error.message);
            }).finally(() => {
                setIsRunning(false);
            });
        }
    }, [open]);

    const handleSave = async (): Promise<any> => {
        let psfIds = selectedPsfItems.map((item) => item.id);
        let searchCondition = condition ? [condition.key, condition.comparator, condition.value].join('||') : null;
        return EventSequenceService.fetchStatsOfOccurrencesLinkedToEventSequence(
            env!.eventSequence!.id,
            forceUTCDate(startValue),
            forceUTCDate(endValue),
            searchCondition,
            psfIds
        );
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperComponent={PaperComponent}
            aria-labelledby='draggable-dialog-title'
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle  style={{ cursor: 'move'}} id='draggable-dialog-title'>
                O-DNA Statistics
            </DialogTitle>
            <DialogContent dividers style={{minHeight: '300px'}}>
                <FormGroup>
                    <Stack direction='row' spacing={2} justifyContent='start'>
                        <InputLabel tabIndex={-1} id={`start-label-${uuidRef.current}`} htmlFor={`start-value-${uuidRef.current}`}>From: </InputLabel>                  
                        <DatePicker
                            id={`start-value-${uuidRef.current}`}
                            showIcon
                            toggleCalendarOnIconClick
                            selectsStart
                            selected={startValue as Date}
                            startDate={startValue as Date}
                            endDate={endValue as Date}
                            onChange={(date: Date | null): void => onChangeStartValue(date)}
                            placeholderText='Select start date'
                        />
                        
                        
                        <InputLabel tabIndex={-1} id={`end-label-${uuidRef.current}`} htmlFor={`end-value-${uuidRef.current}`}>To: </InputLabel>         
                        <DatePicker
                            id={`end-value-${uuidRef.current}`}
                            showIcon
                            toggleCalendarOnIconClick
                            selectsEnd
                            selected={endValue as Date}
                            startDate={startValue as Date}
                            endDate={endValue as Date}  
                            minDate={startValue as Date}
                            onChange={(date: Date | null): void => onChangeEndValue(date)}
                            placeholderText='Select end date'
                        /> 
                    </Stack>
                    <Stack spacing={2} justifyContent='start' sx={{marginTop: '20px'}}>
                        <Autocomplete
                            options={esdPsfTemplate && esdPsfTemplate.psfTemplateItems ? esdPsfTemplate.psfTemplateItems.sort((a,b) => a.category.localeCompare(b.category)) : []}
                            getOptionLabel={(psfItem: PsfTemplateItem): string => psfItem.code ? `${psfItem.name} - ${psfItem.code}` : psfItem.name}
                            renderOption={(props, option: PsfTemplateItem): ReactElement => (
                                <li {...props} key={option.id}>
                                    {option.code ? `${option.name} - ${option.code}` : option.name}
                                </li>
                            )}
                            renderTags={(tagValue: PsfTemplateItem[], getTagProps): any => {
                                return tagValue.map((option, index) => (
                                    <Chip
                                        label={option.code ? `${option.name} - ${option.code}` : option.name}
                                        {...getTagProps({ index })} key={option.id}
                                    />
                                ))
                            }}
                            value={selectedPsfItems}
                            groupBy={(option: PsfTemplateItem): string => option.category}
                            renderInput={(params): any => <TextField {...params} label="PSF Code"/>}
                            onChange={(event: any, newValue: PsfTemplateItem[]): void => {
                                setSelectedPsfItems(newValue);
                            }}
                            limitTags={6}
                            multiple
                            filterSelectedOptions
                        />

                        <ConditionBuilderComponent
                            keys={occurrenceTemplateKeys}
                            initialCondition={condition}
                            onChangeCondition={onChangeCondition}
                        />
                    </Stack>
                </FormGroup>
            </DialogContent>
            <DialogActions>
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={onClose}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={isRunning ? <CircularProgress size={20} /> : null}
                    onClick={async (): Promise<any> => {
                        setIsRunning(true);
                        try {
                            const response = await handleSave();
                            setCurrentCanvasAction({
                                actionType: CanvasActionType.CANVAS_ODNA_STATS,
                                query: response
                            });
                        } catch (error: any) {
                            toast.error(error.message);
                        } finally {
                            setIsRunning(false);
                            onClose();
                        }                                    
                    }}
                    disabled={!CanvasActions.canAddOrEditResource(env) || isRunning}
                >
                    Ok
                </Button>
            </DialogActions>
        </Dialog>
    )
   
}
